// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { SegmentedControl } from '@/components/segmented-control';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { ProjectReportTable } from '@/report/components/project-report-table';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportDateInput } from '@/report/components/report-date-input';
import { useProjectReport } from '@/report/hooks/use-project-report';
import { getReportFileName } from '@/report/utilities/get-report-file-name';
import { getToday } from '@/utilities/date-time';
import { Box, Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useCallback, useRef } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Upcoming projects report';

const groupSchema = z.enum(['customer']);

type Group = z.infer<typeof groupSchema>;

const GROUP_OPTIONS = [
  {
    label: 'Customer',
    value: groupSchema.options[0],
  },
];

const searchSchema = z.object({
  date: z.string().optional(),
  group: z.array(groupSchema).optional(),
});

function getInterval(date: DateTime, count: number) {
  const start = date.plus({ days: 1 });
  const end = date.plus({ days: count });

  if (start < end) {
    return start.until(end);
  }

  return start.until(start);
}

export const Route = createFileRoute('/_app/reports/upcoming-projects')({
  component: UpcomingProjectsReportRoute,
  validateSearch: searchSchema,
});

function UpcomingProjectsReportRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const startDate = search.date ? DateTime.fromISO(search.date) : getToday();

  const { user } = useCurrentUser();
  const { data = [] } = useProjectReport({
    type: 'upcoming',
    start: startDate.toISODate(),
    end: startDate.toISODate(),
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(containerRef);

  // Each cell represents a single day in the report, so an interval can be
  // constructed by adding the length of the 'cells' array to the start date.
  //
  // The 'cells' array of each data element is expected to have the same length,
  // so the length of the first element is used to determine the count.
  const count = data.length > 0 ? data[0].cells.length : 0;
  const interval = getInterval(startDate, count);
  const emptyState = `No upcoming projects as of ${startDate.toLocaleString({ dateStyle: 'medium' })}`;
  const group = search.group ?? ['customer'];

  const handleChangeDate = (date: DateTime | null) => {
    navigate({ search: { ...search, date: date?.toISODate() } });
  };

  const handleChangeGroup = (group: Group[]) => {
    navigate({ search: { ...search, group } });
  };

  const getFileName = useCallback(() => {
    return getReportFileName(
      'upcoming-projects-report',
      user.organization.timezone
    );
  }, [user.organization.timezone]);

  return (
    <>
      <PageContent
        sx={{
          gap: 2,
          flex: 1,
          display: 'grid',
          maxHeight: '100%',
          gridTemplateRows: 'repeat(2, auto) 1fr',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <Stack gridColumn="span 2">
          <ReportBreadcrumbs title={REPORT_TITLE} />
          <Typography variant="h5">{REPORT_TITLE}</Typography>
          <Typography variant="body2" color="text.secondary">
            Includes total number of units planned for projects that were
            upcoming as of the given date
          </Typography>
        </Stack>
        <SegmentedControl
          multiple
          label="Group by"
          value={group}
          options={GROUP_OPTIONS}
          onChange={handleChangeGroup}
        />
        <ReportDateInput
          date={startDate}
          timezone={user.organization.timezone}
          onChange={handleChangeDate}
        />
        <Box ref={containerRef} gridColumn="span 2">
          <ProjectReportTable
            interval={interval}
            data={data}
            group={group}
            emptyState={emptyState}
            maximumHeight={containerSize.height}
            getFileName={getFileName}
          />
        </Box>
      </PageContent>
    </>
  );
}
